import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../hooks';
import { useMutation, gql } from 'urql';
import { RoleName } from '../types';
import { HacpackMutation } from '../types/graph';
import { Link, useHistory } from 'react-router-dom';
import { FaUser } from "react-icons/fa";

const signOutMutation = gql<HacpackMutation, {}>`mutation {
    signOut  {
        isOk
    }
}`;

type UserMenuProps = {
    onSignOut?: () => void
}

const UserMenu = ({ onSignOut }: UserMenuProps) => {
    const { t } = useTranslation('common');

    const [signOutResult, signOut] = useMutation<Pick<HacpackMutation, 'signOut'>, {}>(signOutMutation);

    const { data, fetching: fetchingApproval } = signOutResult;

    const { currentUser, fetchingCurrentUser, reloadCurrentUser, isSuperuser, hasRole } = useCurrentUser();

    const [dropdownOpen, setDropdownOpen] = useState(false as boolean);

    const toggle = useCallback((): void => setDropdownOpen(prevState => !prevState), [setDropdownOpen]);

    const history = useHistory();

    const isAdmin = useMemo(() => hasRole(RoleName.ADMIN), [hasRole]);

    useEffect(() => {
        if (data?.signOut?.isOk === true) {
            reloadCurrentUser();
        }
    }, [data, history, reloadCurrentUser]);

    const userManagementItem = useMemo(() => {
        if (isSuperuser || isAdmin) {
            return <DropdownItem tag={Link} to={`/${t('userManagement.slug')}`} >{t('userManagement.title')}</DropdownItem>;
        }

        return null;
    }, [isSuperuser, isAdmin, t]);

    const disabled = fetchingApproval || fetchingCurrentUser;

    const onSignOutButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        signOut({});
    }, [signOut]);

    const dropdown = useMemo(() => currentUser === null
        ? null
        : <Dropdown isOpen={dropdownOpen} toggle={toggle} disabled={disabled}>
            <DropdownToggle color="link" className="my-2 my-sm-0 ml-1">
                <FaUser />
            </DropdownToggle>
            <DropdownMenu end>
                <DropdownItem header>{currentUser.displayName ?? currentUser.login}</DropdownItem>
                {userManagementItem}
                <DropdownItem divider />
                <DropdownItem onClick={onSignOutButtonClick} disabled={disabled}>{t('access.logoutAction')}</DropdownItem>
            </DropdownMenu>
        </Dropdown>,
        [currentUser, dropdownOpen, toggle, onSignOutButtonClick, userManagementItem, t, disabled]);

    return dropdown;
}

export default UserMenu;